import React, { useState, useEffect } from "react";
import { saleService } from "../../services/saleService";
import { adminService } from "../../services/adminService";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import {
  CurrencyDollarIcon,
  UserIcon,
  CalendarIcon,
  MapPinIcon,
  PhoneIcon,
  EyeIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import LogoLoader from "../Common/LogoLoader";
import PaymentReceipt from "../Common/PaymentReceipt";

const UserSalesAdmin = () => {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [filter, setFilter] = useState("all"); // all, today, week, month (sent as period to API)
  const [statusFilter, setStatusFilter] = useState("all"); // all, processing, paid
  const [selectedSale, setSelectedSale] = useState(null);
  const [receiptSale, setReceiptSale] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [stats, setStats] = useState({
    totalRevenue: 0,
    totalSales: 0,
    paidSales: 0,
    processingSales: 0,
    paidAmount: 0,
    processingAmount: 0
  });
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebouncedValue(searchTerm, 450);
  const [dateFilter, setDateFilter] = useState("");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [pagination, setPagination] = useState({
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20,
  });

  useEffect(() => {
    fetchSales();
  }, [currentPage, debouncedSearch, statusFilter, dateFilter, filter, customStartDate, customEndDate]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter, dateFilter, filter, customStartDate, customEndDate]);

  useEffect(() => {
    if (notification.show) {
      const timer = setTimeout(() => {
        setNotification({ show: false, message: '', type: 'success' });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification.show]);

  const fetchSales = async () => {
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        search: debouncedSearch,
        status: statusFilter !== "all" ? statusFilter : undefined,
        date: filter !== "custom" && dateFilter ? dateFilter : undefined,
        period: filter !== "all" && filter !== "custom" ? filter : undefined,
        ...(filter === "custom" && customStartDate && customEndDate
          ? { startDate: customStartDate, endDate: customEndDate }
          : {}),
      };

      const data = await adminService.getAllSales(params);
      setSales(data.sales || []);
      setStats(data.stats || {});
      setPagination(
        data.pagination || {
          totalPages: 1,
          totalItems: (data.sales || []).length,
          itemsPerPage: itemsPerPage,
        }
      );
    } catch (error) {
      console.error("Error fetching sales:", error);
      setMessage("Error loading sales data");
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success' });
    }, 3000);
  };

  const handleStatusUpdate = async (saleId, newStatus) => {
    try {
      setLoading(true);
      await adminService.updateSaleStatus(saleId, newStatus);
      setNotification({ show: true, type: 'success', message: 'Sale status updated successfully!' });
      setSelectedSale(null);
      fetchSales(); // Refresh the sales list
    } catch (err) {
      console.error("Error updating status:", err);
      setNotification({ show: true, type: 'error', message: 'Failed to update status. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "paid":
        return "text-green-600 bg-green-100";
      case "processing":
        return "text-yellow-600 bg-yellow-100";
      case "cancelled":
        return "text-red-600 bg-red-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LogoLoader />
      </div>
    );
  }

  const totalPages = pagination.totalPages || 1;
  const totalItems = pagination.totalItems ?? sales.length;
  const startIndex = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endIndex = totalItems === 0 ? 0 : Math.min(currentPage * itemsPerPage, totalItems);
  const currentSales = sales;

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Calculate stats with fallback calculation if backend stats are incorrect
  const calculateSaleAmount = (sale) => {
    if (sale.totalPrice && sale.totalPrice > 0) {
      return sale.totalPrice;
    }
    
    if (sale.materials && Array.isArray(sale.materials)) {
      return sale.materials.reduce((sum, material) => {
        const quantity = material.quantity || 0;
        const pricePerKg = material.material?.pricePerKg || 0;
        return sum + (quantity * pricePerKg);
      }, 0);
    }
    
    return 0;
  };

  // Use backend stats if available, otherwise calculate from sales data
  const totalRevenue = stats.totalRevenue && stats.totalRevenue > 0 
    ? stats.totalRevenue 
    : sales.reduce((sum, sale) => sum + calculateSaleAmount(sale), 0);
  
  const totalSales = stats.totalSales || sales.length;
  const paidSales = stats.paidSales || sales.filter(sale => sale.status === 'paid').length;
  const processingSales = stats.processingSales || sales.filter(sale => sale.status === 'processing').length;
  
  const paidAmount = stats.paidAmount && stats.paidAmount > 0 
    ? stats.paidAmount 
    : sales.filter(sale => sale.status === 'paid').reduce((sum, sale) => sum + calculateSaleAmount(sale), 0);
  
  const processingAmount = stats.processingAmount && stats.processingAmount > 0 
    ? stats.processingAmount 
    : sales.filter(sale => sale.status === 'processing').reduce((sum, sale) => sum + calculateSaleAmount(sale), 0);

  const isDefaultPeriodView =
    filter === "all" ||
    (filter === "custom" && (!customStartDate || !customEndDate));

  return (
    <div className="space-y-6">
      {/* Notification */}
      {notification.show && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-md shadow-lg ${
          notification.type === 'success' 
            ? 'bg-green-100 border border-green-400 text-green-700' 
            : 'bg-red-100 border border-red-400 text-red-700'
        }`}>
          <div className="flex items-center">
            <div className="flex-shrink-0">
              {notification.type === 'success' ? (
                <CheckCircleIcon className="h-5 w-5" />
              ) : (
                <ExclamationTriangleIcon className="h-5 w-5" />
              )}
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium">{notification.message}</p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Sales Overview
        </h1>
        <p className="text-gray-600">
          Monitor all sales transactions across the platform.
        </p>
      </div>

      {/* Message */}
      {message && (
        <div className="bg-red-100 text-red-700 border border-red-200 p-4 rounded-md">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
            {message}
          </div>
        </div>
      )}

      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-md shadow-lg ${
          notification.type === 'success' 
            ? 'bg-green-100 text-green-700 border border-green-200' 
            : 'bg-red-100 text-red-700 border border-red-200'
        }`}>
          <div className="flex items-center">
            <div className="ml-3">
              <p className="text-sm font-medium">{notification.message}</p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 min-[480px]:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-3">
        <div className="bg-white rounded-lg shadow p-3 sm:p-4 min-w-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 bg-blue-100 rounded-lg shrink-0">
              <CurrencyDollarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Total Revenue</p>
              <p className="text-base sm:text-xl font-bold text-gray-900 break-words">
                ₦{totalRevenue.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-3 sm:p-4 min-w-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 bg-green-100 rounded-lg shrink-0">
              <UserIcon className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Total Sales</p>
              <p className="text-base sm:text-xl font-bold text-gray-900 break-words">{totalSales}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-3 sm:p-4 min-w-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 bg-green-100 rounded-lg shrink-0">
              <CheckCircleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Paid Sales</p>
              <p className="text-base sm:text-xl font-bold text-gray-900 break-words">
                {paidSales}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-3 sm:p-4 min-w-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 bg-yellow-100 rounded-lg shrink-0">
              <ClockIcon className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Processing Sales</p>
              <p className="text-base sm:text-xl font-bold text-gray-900 break-words">
                {processingSales}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-3 sm:p-4 min-w-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 bg-green-100 rounded-lg shrink-0">
              <CurrencyDollarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Paid Amount</p>
              <p className="text-base sm:text-xl font-bold text-gray-900 break-words">
                ₦{paidAmount.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-3 sm:p-4 min-w-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 bg-yellow-100 rounded-lg shrink-0">
              <CurrencyDollarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Processing Amount</p>
              <p className="text-base sm:text-xl font-bold text-gray-900 break-words">
                ₦{processingAmount.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <h2 className="text-lg font-semibold text-gray-900">
            Sales History ({totalItems})
          </h2>
          <div className="flex flex-col sm:flex-row flex-wrap gap-4 items-stretch sm:items-center">
            <select
              value={filter}
              onChange={(e) => {
                const v = e.target.value;
                setFilter(v);
                if (v !== "custom") {
                  setCustomStartDate("");
                  setCustomEndDate("");
                }
              }}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="custom">Custom range</option>
            </select>
            {filter === "custom" && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-gray-600 whitespace-nowrap">From</span>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <span className="text-sm text-gray-600 whitespace-nowrap">To</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  min={customStartDate || undefined}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
            )}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="all">All status</option>
              <option value="processing">Processing</option>
              <option value="paid">Paid</option>
            </select>
          </div>
        </div>
      </div>

      {/* Sales List */}
      {currentSales.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <CurrencyDollarIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No sales found
          </h3>
          <p className="text-gray-600">
            {isDefaultPeriodView && statusFilter === "all"
              ? "No sales have been made yet."
              : "No sales match the selected filters."}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sale ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Phone
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentSales.map((sale) => (
                  <tr key={sale._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      #{sale._id?.slice(-6) || "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {sale?.user?.firstName || "N/A"}{" "}
                            {sale?.user?.lastName || "N/A"}
                          </div>
                          
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {sale?.user?.phoneNumber || sale.customer_phone || "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ₦{(() => {
                        // Calculate total price from materials if totalPrice is not available or 0
                        if (sale.totalPrice && sale.totalPrice > 0) {
                          return sale.totalPrice.toLocaleString();
                        }
                        
                        if (sale.materials && Array.isArray(sale.materials)) {
                          const calculatedTotal = sale.materials.reduce((sum, material) => {
                            const quantity = material.quantity || 0;
                            const pricePerKg = material.material?.pricePerKg || 0;
                            return sum + (quantity * pricePerKg);
                          }, 0);
                          return calculatedTotal.toLocaleString();
                        }
                        
                        return "0";
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                          sale.status
                        )}`}
                      >
                        {sale.status?.charAt(0).toUpperCase() +
                          sale.status?.slice(1) || "Unknown"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(sale.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {sale.status === "paid" && sale.receipt && (
                        <button
                          type="button"
                          onClick={() => setReceiptSale(sale)}
                          className="mr-3 text-sm font-semibold text-green-700"
                        >
                          View receipt
                        </button>
                      )}
                      <button
                        onClick={() => setSelectedSale(sale)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Sale Details Modal */}
      {selectedSale && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Sale Details #{selectedSale._id?.slice(-10)}
                </h3>
                <button
                  onClick={() => setSelectedSale(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Customer Name
                    </label>
                    <p className="text-sm text-gray-900">
                      {selectedSale.user.firstName || "N/A"}{" "}
                      {selectedSale.user.lastName || "N/A"}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Phone
                    </label>
                    <p className="text-sm text-gray-900">
                      {selectedSale.user?.phoneNumber || selectedSale.customer_phone || "N/A"}
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Address
                  </label>
                  <p className="text-sm text-gray-900">
                    {selectedSale.user?.homeAddress || selectedSale.customer_address || "N/A"}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Total Amount
                    </label>
                    <p className="text-lg font-semibold text-green-600">
                      ₦{(() => {
                        // Calculate total price from materials if totalPrice is not available or 0
                        if (selectedSale.totalPrice && selectedSale.totalPrice > 0) {
                          return selectedSale.totalPrice.toFixed(2);
                        }
                        
                        if (selectedSale.materials && Array.isArray(selectedSale.materials)) {
                          const calculatedTotal = selectedSale.materials.reduce((sum, material) => {
                            const quantity = material.quantity || 0;
                            const pricePerKg = material.material?.pricePerKg || 0;
                            return sum + (quantity * pricePerKg);
                          }, 0);
                          return calculatedTotal.toFixed(2);
                        }
                        
                        return "0.00";
                      })()}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Status
                    </label>
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                        selectedSale.status
                      )}`}
                    >
                      {selectedSale.status?.charAt(0).toUpperCase() +
                        selectedSale.status?.slice(1) || "Unknown"}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Date
                  </label>
                  <p className="text-sm text-gray-900">
                    {new Date(selectedSale.createdAt).toLocaleDateString()} at{" "}
                    {new Date(selectedSale.createdAt).toLocaleTimeString()}
                  </p>
                </div>

                {selectedSale.bankDetails && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Bank Account Details
                    </label>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Bank Name
                          </label>
                          <p className="text-sm text-gray-900 mt-1">
                            {selectedSale.bankDetails.bankName || "N/A"}
                          </p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Account Number
                          </label>
                          <p className="text-sm text-gray-900 mt-1">
                            {selectedSale.bankDetails.accountNumber || "N/A"}
                          </p>
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Account Name
                          </label>
                          <p className="text-sm text-gray-900 mt-1">
                            {selectedSale.bankDetails.accountName || "N/A"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {selectedSale.materials &&
                  selectedSale.materials.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Materials
                      </label>
                      <div className="space-y-2">
                        {selectedSale.materials.map((item, index) => (
                          <div
                            key={index}
                            className="flex justify-between items-center p-2 bg-gray-50 rounded"
                          >
                            <span className="text-sm text-gray-900">
                              {item.material.name || "Unknown Material"}
                            </span>
                            <div className="flex items-center space-x-4">
                              <span className="text-sm text-gray-600">
                                {item.quantity}kg
                              </span>
                              <span className="text-sm text-gray-600">
                                ₦{item.price?.toFixed(2) || "0.00"}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                {selectedSale.notes && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Notes
                    </label>
                    <p className="text-sm text-gray-900">
                      {selectedSale.notes}
                    </p>
                  </div>
                )}
              </div>
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Update Payment Status
                </label>
                <select
                  value={selectedSale.status}
                  onChange={(e) =>
                    setSelectedSale({ ...selectedSale, status: e.target.value })
                  }
                  className="block p-3 w-full mt-1 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                >
                  <option value="processing">Processing</option>
                  <option value="paid">Paid</option>
                </select>

                {/* Optional save button */}
                <button
                  disabled={loading}
                  onClick={() =>
                    handleStatusUpdate(selectedSale._id, selectedSale.status)
                  }
                  className="mt-4 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md"
                >
                  Save
                </button>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setSelectedSale(null)}
                  className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-md"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && totalItems > 0 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{startIndex}</span> to{' '}
                <span className="font-medium">{endIndex}</span> of{' '}
                <span className="font-medium">{totalItems}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      page === currentPage
                        ? 'z-10 bg-green-50 border-green-500 text-green-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
      {receiptSale && (
        <PaymentReceipt
          title="Seller payment receipt"
          receipt={receiptSale.receipt}
          amount={receiptSale.totalPrice}
          onClose={() => setReceiptSale(null)}
        />
      )}
    </div>
  );
};

export default UserSalesAdmin;
